import { Button, Modal, Space, Spin, Tooltip, Typography } from "antd";
import { KeyRound, ShieldAlert, Smartphone } from "lucide-react";

import { isPasskeySupported } from "../../utils/webauthn";
import ReauthenticateModal from "../security/ReauthenticateModal";
import SecurityEnrollmentModal from "../security/SecurityEnrollmentModal";
import { useMfaRequired } from "../security/useMfaRequired";

/** The classic UI's take on a project that requires two-step verification; the flow is shared with the new UI */
const MfaRequiredModal = () => {
    const flow = useMfaRequired();
    const { overview } = flow;
    const who = flow.projectName || "This project";

    const footer = [
        <Button key="later" onClick={flow.close}>Not now</Button>,
        ...(flow.mustSignIn ? [<Button key="sign-in" type="primary" onClick={flow.signIn}>Sign in again</Button>] : []),
        ...(!flow.loading && !flow.mustSignIn && flow.hasFactors ? [<Button key="continue" type="primary" onClick={flow.confirm}>Continue</Button>] : []),
    ];

    return (
        <>
            <Modal
                title="Two-step verification required"
                centered
                open={flow.visible && flow.step === "required"}
                onCancel={flow.close}
                zIndex={1001}   // always display on top (default is 1000)
                footer={footer}
            >
                <Space direction="vertical" size="middle" style={{ width: "100%" }}>
                    <Space align="start" size="middle">
                        <ShieldAlert size={24} />
                        <Typography.Text>
                            {who} asks every member to use two-step verification.{" "}
                            {flow.mustSignIn
                                ? flow.federated
                                    ? "Sign in again with Google or GitHub. Your provider's two-step verification counts here."
                                    : "Sign in again with two-step verification to continue."
                                : flow.hasFactors
                                    ? "Confirm it's you with your passkey or authenticator app. You'll stay on this page."
                                    : !flow.loading && "Add one now, then confirm with it. It takes about a minute."}
                        </Typography.Text>
                    </Space>
                    {flow.loading && <Spin />}
                    {!flow.loading && !flow.mustSignIn && !flow.hasFactors && (
                        <Space direction="vertical" style={{ width: "100%" }}>
                            {overview?.passkeysAvailable && (
                                <Tooltip title={isPasskeySupported() ? undefined : "This browser doesn't support passkeys"}>
                                    <Button block size="large" icon={<KeyRound size={18} />} disabled={!isPasskeySupported()} onClick={() => flow.enroll("passkey")}>
                                        Passkey
                                    </Button>
                                </Tooltip>
                            )}
                            {overview?.totpAvailable && (
                                <Button block size="large" icon={<Smartphone size={18} />} onClick={() => flow.enroll("totp")}>
                                    Authenticator app
                                </Button>
                            )}
                        </Space>
                    )}
                </Space>
            </Modal>
            <SecurityEnrollmentModal
                open={flow.visible && flow.step === "enroll"}
                initialMethod={flow.method}
                onComplete={flow.enrollmentComplete}
                onClose={flow.enrollmentClosed}
            />
            <ReauthenticateModal
                open={flow.visible && flow.step === "confirm"}
                description={`Confirm it's you with two-step verification to open ${flow.projectName || "the project"}.`}
                onSuccess={flow.confirmed}
                onCancel={flow.close}
            />
        </>
    );
};

export default MfaRequiredModal;
